import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import ReactDOMServer from 'react-dom/server';
import api from '../utils/api';
import { CyclePrintView } from '../components/CyclePrintView';

interface CycleReportData {
  // compliance fields
  cycleId: string;
  cycleName: string;
  professionID: string;
  professionName: string;
  stateID: number;
  stateName: string;
  totalEarned: number;
  totalRequired: number;
  percentComplete: number;
  isMet: boolean;
  categories: any[];
  warnings: string[];
  daysRemaining: number;
  cycleStartDate: string;
  cycleEndDate: string;
  // extended fields
  classes: { _id: string; classname: string; classdate: string; classLU: number; took: boolean }[];
  totalLUPlanned: number;
  luToBeCompleted: number;
  licenseNumber: string;
  creditTerm: string;
  isDentistProfession: boolean;
  note: string;
}

interface ComplianceReportData {
  userName: string;
  firstName: string;
  lastName: string;
  userId: string;
  cycles: CycleReportData[];
  generatedAt: string;
}

interface CompanyInfo {
  name: string;
  street: string;
  city: string;
  addressState: string;
  zip: string;
  phone: string;
  website: string;
}

const fmtDate = (val: string | undefined) => {
  if (!val) return '—';
  const d = new Date(val);
  if (isNaN(d.getTime())) return val;
  return `${String(d.getUTCMonth() + 1).padStart(2, '0')}/${String(d.getUTCDate()).padStart(2, '0')}/${d.getUTCFullYear()}`;
};

const UserComplianceReport: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Letterhead only applies when accessed from the team report ("Full Report →")
  const isTeamContext = searchParams.get('context') === 'team';
  const [reportData, setReportData] = useState<ComplianceReportData | null>(null);
  const [company, setCompany] = useState<CompanyInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [printing, setPrinting] = useState(false);
  const printContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchReportData();
  }, [userId]);

  // Always attempt company fetch — display is gated on isTeamContext in handlePrint.
  // Keeping this separate from [userId] effect avoids closure capture issues with
  // searchParams (which may hydrate on a separate render pass).
  useEffect(() => {
    api.get('/api/company/mine').then(r => setCompany(r.data)).catch(() => {});
  }, []);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/cycles/user/${userId}/compliance-data`);
      setReportData(response.data);
    } catch (err: any) {
      console.error('Error fetching compliance report:', err);
      setError(err.response?.data?.error || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (printing || !reportData) return;
    setPrinting(true);

    // Open popup the same way CycleDetail does
    const printWindow = window.open(
      'about:blank',
      'PrintWindow_' + Date.now(),
      'width=900,height=700,menubar=yes,toolbar=no,location=no,status=no'
    );
    if (!printWindow) {
      alert('Could not open print window. Please allow popups for this site.');
      setPrinting(false);
      return;
    }

    // Render one CyclePrintView per cycle, separated by page-breaks
    const cyclesHtml = reportData.cycles.map((cycle, idx) => {
      const period = `${fmtDate(cycle.cycleStartDate)} – ${fmtDate(cycle.cycleEndDate)}`;

      // Map categories to the shape CyclePrintView expects
      const categoryCompliance = (cycle.categories || []).map((cat: any) => ({
        categoryTag:  cat.categoryTag,
        categoryName: cat.categoryName || cat.categoryTag,
        description:  cat.description || '',
        minCredits:   cat.required ?? 0,
        maxCredits:   cat.max,
        earned:       cat.earned ?? 0,
        planned:      0,
      }));

      const html = ReactDOMServer.renderToStaticMarkup(
        <CyclePrintView
          cycleName={cycle.cycleName}
          state={cycle.stateName}
          period={period}
          creditsRequired={cycle.totalRequired}
          totalLUCompleted={cycle.totalEarned}
          totalLUPlanned={cycle.totalLUPlanned}
          luToBeCompleted={cycle.luToBeCompleted}
          attachedClasses={cycle.classes || []}
          firstName={reportData.firstName}
          lastName={reportData.lastName}
          professionName={cycle.professionName}
          licenseNumber={cycle.licenseNumber}
          creditTerm={cycle.creditTerm || 'CEC'}
          categoryCompliance={categoryCompliance}
          isDentistProfession={cycle.isDentistProfession}
          note={cycle.note || undefined}
        />
      );

      // Extract <style> tags from the rendered HTML so they go into <head>
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      const styleTags = tempDiv.querySelectorAll('style');
      let extractedStyles = '';
      styleTags.forEach(s => { extractedStyles += s.innerHTML + '\n'; s.remove(); });

      return { bodyHtml: tempDiv.innerHTML, styles: extractedStyles, isLast: idx === reportData.cycles.length - 1 };
    });

    // Combine all styles (deduplicated by content)
    // All cycles use the same CyclePrintView so styles are identical — just take the first one
    const allStyles = cyclesHtml.length > 0 ? cyclesHtml[0].styles : '';

    // Build body: cycles separated by page-break divs
    const bodyContent = cyclesHtml.map((c, i) =>
      i < cyclesHtml.length - 1
        ? c.bodyHtml + '<div style="page-break-after:always;break-after:page;"></div>'
        : c.bodyHtml
    ).join('\n');

    const memberName = reportData.userName || `${reportData.firstName} ${reportData.lastName}`.trim();

    // Build compact 2-line company letterhead (omit if no company data)
    const letterheadHtml = company?.name ? (() => {
      const addressParts = [
        company.street,
        [company.city, company.addressState].filter(Boolean).join(', ') + (company.zip ? ` ${company.zip}` : ''),
        company.phone,
        company.website,
      ].filter(Boolean);
      const addressLine = addressParts.join(' · ');
      return `
<div style="border-top:3px solid #0f1f3d;padding:10pt 0 0;margin-bottom:0;display:flex;justify-content:space-between;align-items:flex-start;">
  <div>
    <div style="font-family:'Fraunces',Georgia,serif;font-size:13pt;font-weight:700;color:#000;letter-spacing:-0.01em;line-height:1.15;">${company.name}</div>
    ${addressLine ? `<div style="font-family:'IBM Plex Sans',Arial,sans-serif;font-size:7.5pt;color:#333;margin-top:3pt;letter-spacing:0.01em;">${addressLine}</div>` : ''}
  </div>
  <div style="text-align:right;font-family:'IBM Plex Sans',Arial,sans-serif;">
    <div style="font-size:9pt;font-weight:700;color:#0f1f3d;letter-spacing:-0.01em;">CEC Track</div>
    <div style="font-size:6.5pt;color:#888;margin-top:1pt;">cectrack.com</div>
  </div>
</div>
<div style="border-top:0.75pt solid #bbb;margin:8pt 0 14pt;"></div>`;
    })() : '';

    printWindow.document.open();
    printWindow.document.write(`<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8">
    <title>CE Compliance Report – ${memberName}</title>
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link href="https://fonts.googleapis.com/css2?family=Fraunces:wght@700&family=IBM+Plex+Sans:wght@400;600&display=swap" rel="stylesheet">
    <style>
      body { margin: 0; padding: 0; font-family: Arial, sans-serif; }
      img  { max-width: 100%; height: auto; }
      ${allStyles}
      @media print {
        @page {
          margin: 0.5in 0.5in 0.75in 0.5in;
          @bottom-left   { content: "© ${new Date().getFullYear()} CEC Track, Inc."; font-size: 9pt; color: #666; }
          @bottom-center { content: "v3.3"; font-size: 7pt; color: #999; }
          @bottom-right  { content: "Page " counter(page) " of " counter(pages); font-size: 9pt; color: #666; }
        }
      }
    </style>
  </head>
  <body>
    ${letterheadHtml}${bodyContent}
    <script>
      window.onload = function() {
        setTimeout(function() { window.print(); }, 300);
      };
    <\/script>
  </body>
</html>`);
    printWindow.document.close();
    setPrinting(false);
  };

  if (loading) {
    return (
      <div className="container mt-4 text-center">
        <div className="spinner-border" role="status"><span className="visually-hidden">Loading…</span></div>
        <p className="mt-3">Loading compliance report…</p>
      </div>
    );
  }

  if (error || !reportData) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">
          <h4>Error</h4>
          <p>{error || 'Report not found'}</p>
          <button className="btn btn-primary" onClick={() => navigate(-1)}>Go Back</button>
        </div>
      </div>
    );
  }

  const compliantCount = reportData.cycles.filter(c => c.isMet).length;
  const total = reportData.cycles.length;

  return (
    <div className="container mt-4">

      {/* ── Top action bar ── */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <button className="btn btn-outline-secondary" onClick={() => navigate(-1)}>
          <i className="bi bi-arrow-left me-2"></i>Back
        </button>
        <button
          className="btn btn-primary"
          onClick={handlePrint}
          disabled={printing}
        >
          <i className="bi bi-printer me-2"></i>
          {printing ? 'Preparing…' : 'Print / Save PDF'}
        </button>
      </div>

      {/* ── Report header ── */}
      <div className="card mb-4">
        <div className="card-body text-center py-4">
          <h2 className="mb-1">Continuing Education Compliance Report</h2>
          <h4 className="text-muted mb-2">{reportData.userName}</h4>
          <small className="text-muted">
            Generated:{' '}
            {new Date(reportData.generatedAt || Date.now()).toLocaleDateString('en-US', {
              year: 'numeric', month: 'long', day: 'numeric',
              hour: '2-digit', minute: '2-digit',
            })}
          </small>
        </div>
      </div>

      {/* ── Summary strip ── */}
      <div className="card mb-4">
        <div className="card-header bg-primary text-white">
          <h5 className="mb-0">Summary</h5>
        </div>
        <div className="card-body">
          <div className="row text-center">
            <div className="col-md-3">
              <h4>{total}</h4>
              <p className="text-muted mb-0">Active Cycles</p>
            </div>
            <div className="col-md-3">
              <h4 className="text-success">{compliantCount}</h4>
              <p className="text-muted mb-0">Compliant</p>
            </div>
            <div className="col-md-3">
              <h4 className="text-danger">{total - compliantCount}</h4>
              <p className="text-muted mb-0">In Progress / Non-Compliant</p>
            </div>
            <div className="col-md-3">
              <h4>{total > 0 ? Math.round((compliantCount / total) * 100) : 0}%</h4>
              <p className="text-muted mb-0">Compliance Rate</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Per-cycle detail (screen view) ── */}
      {reportData.cycles.length > 0 ? (
        reportData.cycles.map((cycle, idx) => (
          <div key={idx} className="card mb-4">
            <div className={`card-header ${cycle.isMet ? 'bg-success' : 'bg-warning'} text-white`}>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">{cycle.cycleName}</h5>
                <span>{cycle.isMet ? '✓ Compliant' : '⚠ In Progress'}</span>
              </div>
              <small className="opacity-75">{cycle.professionName} · {cycle.stateName}</small>
            </div>
            <div className="card-body">
              <div className="row mb-2">
                <div className="col-md-4"><strong>Period:</strong> {fmtDate(cycle.cycleStartDate)} – {fmtDate(cycle.cycleEndDate)}</div>
                <div className="col-md-4"><strong>Credits:</strong> {cycle.totalEarned} / {cycle.totalRequired} {cycle.creditTerm}</div>
                <div className="col-md-4"><strong>Progress:</strong> {cycle.percentComplete}%{cycle.daysRemaining >= 0 && <span className="text-muted ms-2">({cycle.daysRemaining} days left)</span>}</div>
              </div>
              {cycle.warnings && cycle.warnings.length > 0 && (
                <div className="alert alert-warning py-2 mb-2">
                  <strong>⚠ Warnings:</strong>
                  <ul className="mb-0 mt-1 small">
                    {cycle.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}
              <div className="text-muted small">{cycle.classes?.length || 0} classes in cycle</div>
            </div>
          </div>
        ))
      ) : (
        <div className="alert alert-info">No active cycles found for this user.</div>
      )}

      {/* Hidden print-content node (not used — popup renders directly from data) */}
      <div ref={printContentRef} style={{ display: 'none' }} />
    </div>
  );
};

export default UserComplianceReport;
